<template>
  <!-- The search the user just ran, with a strip of what it turned up and
  one result already saved. Context: keyword, adThumbnails (up to 3) -->
  <div>
    <!-- Search bar -->
    <div class="flex items-center gap-2 px-3 py-2 border-b border-border-normal">
      <SearchIcon
        :width="14"
        :height="14"
        class="text-icon-normal flex-shrink-0"
      />
      <BaseText
        type="label"
        size="sm"
        class="text-text-loud truncate"
      >
        {{ keyword }}
      </BaseText>
      <span class="scene-caret" />
      <div class="ml-auto flex items-center gap-1 flex-shrink-0">
        <span class="scene-filter-chip">Most Relevant</span>
        <span class="scene-filter-chip">Video</span>
      </div>
    </div>
    <!-- Result strip -->
    <div class="flex items-stretch gap-2 px-3 pt-3 pb-3">
      <div
        v-for="(tile, index) in tiles"
        :key="`tile-${index}`"
        class="scene-tile relative flex-1 rounded-lg overflow-hidden bg-neutral-50"
        :style="{ animationDelay: `${160 + index * 90}ms` }"
      >
        <img
          v-if="tile.thumbnail"
          :src="tile.thumbnail"
          alt=""
          class="w-full h-full object-cover"
        >
        <div
          v-else
          class="w-full h-full scene-tile-placeholder"
          :class="`scene-tile-placeholder--${index}`"
        />
        <!-- Save button state: the last tile has been saved -->
        <div
          class="absolute left-1.5 right-1.5 bottom-1.5 flex items-center justify-center gap-1 py-1 rounded-md"
          :class="tile.saved ? 'scene-save--done' : 'scene-save'"
        >
          <svg
            v-if="tile.saved"
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="10"
            viewBox="0 0 20 20"
            fill="none"
          >
            <path
              d="M4.5 10.5L8 14L15.5 6.5"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <SaveAdIcon
            v-else
            :width="10"
            :height="10"
          />
          <span class="scene-save-text">{{ tile.saved ? 'Saved' : 'Save' }}</span>
        </div>
      </div>
    </div>
    <!-- Footer count -->
    <div class="px-3 pb-2.5 -mt-1">
      <BaseText
        type="body"
        size="xs"
        class="text-text-subdued"
      >
        Searching 200M+ ads · results update as brands launch
      </BaseText>
    </div>
  </div>
</template>

<script>
import SearchIcon from '../../globals/Icons/SearchIcon.vue'
import SaveAdIcon from '../../globals/Icons/SaveAdIcon.vue'

const TILE_COUNT = 3

export default {
  name: 'DiscoverySearchScene',
  components: {
    SearchIcon,
    SaveAdIcon
  },
  props: {
    context: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    keyword () {
      return this.context.keyword || 'your keyword'
    },
    tiles () {
      const thumbnails = (this.context.adThumbnails || []).filter(Boolean)
      return Array.from({ length: TILE_COUNT }, (_, index) => ({
        thumbnail: thumbnails[index] || null,
        saved: index === TILE_COUNT - 1
      }))
    }
  }
}
</script>

<style scoped>
.scene-caret {
  width: 1px;
  height: 14px;
  background-color: theme('colors.primary.purple-100');
  animation: scene-caret-blink 1s steps(2, start) infinite;
}
.scene-filter-chip {
  font-size: 9px;
  font-weight: 500;
  line-height: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  color: theme('colors.neutral.600');
  background-color: theme('colors.neutral.25');
}
.scene-tile {
  height: 92px;
  animation: scene-tile-in 420ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.scene-tile-placeholder--0 {
  background: linear-gradient(160deg, theme('colors.primary.purple-25') 0%, theme('colors.primary.purple-50') 100%);
}
.scene-tile-placeholder--1 {
  background: linear-gradient(160deg, theme('colors.primary.blue-25') 0%, theme('colors.primary.blue-50') 100%);
}
.scene-tile-placeholder--2 {
  background: linear-gradient(160deg, theme('colors.primary.red-25') 0%, theme('colors.primary.red-50') 100%);
}
.scene-save {
  color: theme('colors.neutral.600');
  background-color: rgba(255, 255, 255, 0.92);
}
.scene-save--done {
  color: theme('colors.white');
  background-color: theme('colors.primary.green-100');
  animation: scene-save-pop 360ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
  animation-delay: 520ms;
}
.scene-save-text {
  font-size: 9px;
  font-weight: 600;
  line-height: 12px;
}
@keyframes scene-caret-blink {
  to {
    visibility: hidden;
  }
}
@keyframes scene-tile-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes scene-save-pop {
  from {
    transform: scale(0.85);
  }
  to {
    transform: scale(1);
  }
}
@media (prefers-reduced-motion: reduce) {
  .scene-caret,
  .scene-tile,
  .scene-save--done {
    animation: none;
  }
}
</style>
